/**
 * canvas-engine/css/scopeCustomCss.js
 *
 * OWN section selector scoper for the Enterprise Canvas Builder.
 *
 * ZERO REUSE: this module imports NOTHING from
 *   - admin-console-ui/src/modules/template-builder/
 *   - academy-ui/src/template/
 * and is deliberately NOT the existing `SectionCustomScope`. It only reuses the
 * sibling OWN sanitizer in this same canvas-engine module.
 *
 * Decision (design Open Q3): own scoped id-prefix + `isolation: isolate` + own
 * sanitization (NOT Shadow DOM). The owning section is expected to render inside
 * an element with `id="${scopeId}"` and `isolation: isolate`. This function
 * sanitizes the author CSS and then rewrites EVERY rule selector to be prefixed
 * by `#${scopeId} ` so no rule can ever apply page-globally.
 *
 * Pure and deterministic (byte-identical output for the same input) so the
 * Console Preview (builder) and the published Reader produce exactly the same
 * scoped CSS. (Requirements 26.1, 26.2, 26.3)
 */

const { parseSafeRules } = require('./sanitizeCss');

/**
 * Normalize a caller-supplied scope id into the raw id token (no leading `#`).
 * Defensive: callers always pass an id, but we coerce/trim so the output is
 * stable and never produces a malformed `# ` prefix.
 * @param {*} scopeId
 * @returns {string}
 */
function normalizeScopeId(scopeId) {
  if (scopeId === null || scopeId === undefined) return '';
  let id = String(scopeId).trim();
  if (id.charAt(0) === '#') id = id.slice(1).trim();
  return id;
}

/**
 * Split a selector list on top-level commas only — commas nested inside
 * parentheses (e.g. `:not(a, b)`, `:is(h1, h2)`) must NOT split the selector.
 * @param {string} selector
 * @returns {string[]} individual selector parts (trimmed, empties removed)
 */
function splitSelectorList(selector) {
  const parts = [];
  let depth = 0;
  let current = '';
  for (let i = 0; i < selector.length; i += 1) {
    const ch = selector[i];
    if (ch === '(') {
      depth += 1;
      current += ch;
    } else if (ch === ')') {
      if (depth > 0) depth -= 1;
      current += ch;
    } else if (ch === ',' && depth === 0) {
      parts.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) parts.push(current.trim());
  return parts.filter((p) => p.length > 0);
}

/**
 * Prefix every comma-separated part of a selector with `#${scopeId} `.
 * @param {string} selector
 * @param {string} scopeId raw id token (no leading `#`)
 * @returns {string} scoped selector list
 */
function prefixSelector(selector, scopeId) {
  const prefix = `#${scopeId}`;
  return splitSelectorList(selector)
    .map((part) => `${prefix} ${part}`)
    .join(', ');
}

/**
 * Sanitize the author CSS and scope every surviving rule to the owning section.
 *
 * - Unscoped rules, at-rules, empty rules, and dangerous declarations are
 *   dropped by the shared sanitizer (parseSafeRules).
 * - Every remaining rule selector is rewritten to be prefixed by `#${scopeId} `
 *   so the rule can only match elements inside the section's scope container.
 *
 * @param {string} css author-provided custom CSS
 * @param {string} scopeId the owning section's scope id (with or without `#`)
 * @returns {string} sanitized, section-scoped CSS text ('' when empty)
 */
function scopeCustomCss(css, scopeId) {
  const id = normalizeScopeId(scopeId);
  // Without a usable scope id we cannot guarantee scoping, so emit nothing
  // rather than risk page-global rules.
  if (!id) return '';

  const rules = parseSafeRules(css);
  if (rules.length === 0) return '';

  return rules
    .map((rule) => `${prefixSelector(rule.selector, id)} { ${rule.body} }`)
    .join('\n');
}

module.exports = {
  scopeCustomCss,
  // Exported for unit/property testing of the lower-level pieces.
  splitSelectorList,
  prefixSelector,
  normalizeScopeId,
};
